import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useHeartStore } from '@/stores/heartStore';
import TierBadge from '@/components/TierBadge';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import GoogleTranslateSlot from '@/components/GoogleTranslateSlot';
import FilterAccordion, { type PageKey, PAGE_FILTER_CONFIG } from '@/components/FilterAccordion';
import { useT } from '@/lib/i18n';
import { T } from '@/components/T';
import TextEditOverlay from '@/components/TextEditOverlay';
import { tierLevel } from '@/lib/tierNames';
import MenuActivity from '@/components/insights/MenuActivity';

const APP_VERSION = __APP_VERSION__;

/*
 * IMPORTANT: When adding a new menu item here, also add its key to
 * ALL_MENU_KEYS in api/src/modules/tiers/service.ts so the tier editor
 * picks it up automatically on next API restart.
 *
 * Menu key mapping (used in isMenuAllowed() calls below):
 *   /map -> 'map', /discover -> 'discover', /routes -> 'routes',
 *   /profiles -> 'profiles', /artradar -> 'artradar', /events -> 'events',
 *   /profiles/my -> 'my_profile', /map/my-pins -> 'my_pins',
 *   /artworks/my -> 'my_artworks', /routes/my -> 'my_routes',
 *   /bookmarks -> 'bookmarks', /profiles/import -> 'import_profile', /docs -> 'docs'
 */

const ArtRadarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" style={{ verticalAlign: '-2px' }}>
    <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="8" cy="8" r="3.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
    <line x1="8" y1="8" x2="13.2" y2="2.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="8" cy="8" r="1.3" fill="currentColor"/>
    <circle cx="5" cy="10.8" r="1" fill="currentColor" opacity="0.6"/>
  </svg>
);

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, menuAccess, isImpersonating, returnToAdmin } = useAuthStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());
  const navRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const currentPath = location.pathname;
  const t = useT();

  // Google Translate adds a banner at viewport top and shifts <body> down by its
  // height (40px on desktop, ~70px on mobile because text wraps). The sticky
  // navbar moves with body, but our position:fixed search/filter bar is anchored
  // to the viewport and ends up behind the navbar — so we mirror body.style.top
  // into a CSS variable that the bar adds to its own top offset.
  useEffect(() => {
    const root = document.documentElement;
    const updateOffset = () => {
      const bodyTop = parseFloat(document.body.style.top || '0') || 0;
      root.style.setProperty('--gt-banner-offset', bodyTop + 'px');
    };
    updateOffset();
    const observer = new MutationObserver(updateOffset);
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', updateOffset);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOffset);
    };
  }, []);

  // Pages that show the unified search toolbar + filter accordion
  // Show on all pages EXCEPT admin, pricing, contact, legal, login, docs
  const EXCLUDED_PREFIXES = ['/admin', '/pricing', '/contact', '/legal', '/login', '/register', '/docs', '/forgot'];
  const ADMIN_SEARCH_PAGES = ['/admin/content'];
  const showToolbar = useMemo(() => {
    if (ADMIN_SEARCH_PAGES.some((p) => currentPath.startsWith(p))) return true;
    return !EXCLUDED_PREFIXES.some((p) => currentPath.startsWith(p));
  }, [currentPath]);
  // Find the best matching page config for filter options
  const filterPage = useMemo(() => {
    const keys = Object.keys(PAGE_FILTER_CONFIG) as PageKey[];
    return keys.find((k) => currentPath === k || currentPath.startsWith(k + '/')) || '/discover';
  }, [currentPath]) as PageKey;

  const isAdmin = user?.role === 'platform_admin' || user?.role === 'superadmin' || user?.role === 'moderator' || user?.role === 'admin';

  // Guided registration: while it is unfinished, a banner leads back to /welcome.
  const [setupStatus, setSetupStatus] = useState<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated) { setSetupStatus(null); return; }
    let stop = false;
    import('@/lib/api').then(({ api }) => {
      api.get<{ status: string }>('/onboarding/status')
        .then((s) => { if (!stop) setSetupStatus(s.status); })
        .catch(() => {});
    });
    return () => { stop = true; };
  }, [isAuthenticated, location.pathname]);
  const setupIncomplete = !!setupStatus && setupStatus !== 'active' && setupStatus !== 'mv_pilot_active';

  // Messaging access (superadmin-only until the admin opens it to tiers) + unread badge
  const [canMessage, setCanMessage] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  useEffect(() => {
    if (!isAuthenticated) { setCanMessage(false); return; }
    let stop = false;
    import('@/lib/api').then(({ api }) => {
      api.get<{ canUse: boolean; enabled?: boolean }>('/messaging/access')
        // Hidden when the tier withholds messaging OR the user switched it off.
        .then((a) => { if (!stop) setCanMessage(!!a.canUse && a.enabled !== false); })
        .catch(() => {});
    });
    return () => { stop = true; };
  }, [isAuthenticated]);
  useEffect(() => {
    if (!canMessage) return;
    let stop = false;
    const poll = () => import('@/lib/api').then(({ api }) =>
      api.get<{ unread: number }>('/messaging/unread').then((r) => { if (!stop) setUnreadMsgs(r.unread || 0); }).catch(() => {}));
    poll();
    const h = setInterval(poll, 30000);
    return () => { stop = true; clearInterval(h); };
  }, [canMessage, currentPath]);

  // Hearts: load the member's saved items once, so every heart shows its state.
  useEffect(() => { useHeartStore.getState().load(isAuthenticated ? user?.id : null); }, [isAuthenticated, user?.id]);

  // ♡ Interests unread number — shown only when something is new.
  const [interestsUnread, setInterestsUnread] = useState(0);
  useEffect(() => {
    if (!isAuthenticated) { setInterestsUnread(0); return; }
    let stop = false;
    import('@/lib/api').then(({ api }) =>
      api.get<{ count: number }>('/interests/unread').then((r) => { if (!stop) setInterestsUnread(r.count || 0); }).catch(() => {}));
    return () => { stop = true; };
  }, [isAuthenticated, currentPath]);

  /**
   * Check if a menu item is allowed by the user's tier.
   * Admins always bypass. Unauthenticated users see public items.
   * Items not in menuAccess default to HIDDEN for authenticated users
   * (safe default — new menu keys won't leak until tier sync runs).
   */
  const isMenuAllowed = useCallback((menuKey: string): boolean => {
    if (!isAuthenticated) return true; // Public pages shown to everyone
    if (isAdmin) return true; // Admins bypass tier restrictions
    return menuAccess[menuKey] === true;
  }, [isAuthenticated, isAdmin, menuAccess]);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdowns(new Set());
  }, [currentPath]);

  // Keep spacer height in sync with fixed search/filter bar
  useEffect(() => {
    const sticky = document.getElementById('search-filter-sticky');
    const spacer = document.getElementById('search-filter-spacer');
    if (!sticky || !spacer) return;
    const sync = () => { spacer.style.height = sticky.offsetHeight + 'px'; };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(sticky, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', sync);
    return () => { observer.disconnect(); window.removeEventListener('resize', sync); };
  });

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        navRef.current &&
        toggleRef.current &&
        !navRef.current.contains(e.target as Node) &&
        !toggleRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
        setOpenDropdowns(new Set());
      }
    }
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const toggleDropdown = useCallback((name: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdowns(prev => {
      const next = new Set<string>();
      if (!prev.has(name)) next.add(name);
      return next;
    });
  }, []);

  const handleNavLinkClick = useCallback(() => {
    if (window.innerWidth < 768) {
      setMenuOpen(false);
      setOpenDropdowns(new Set());
    }
  }, []);

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    await logout();
    navigate('/auth/login');
  };

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (document.referrer && new URL(document.referrer).origin === window.location.origin) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  // Active state helpers matching the EJS logic
  const isExploreActive = ['/map', '/discover', '/routes'].some(
    p => currentPath === p || (currentPath.startsWith(p + '/') && !currentPath.startsWith('/routes/my'))
  );
  const isArtWorldActive = ['/profiles', '/artradar', '/events'].some(p => currentPath.startsWith(p));
  const isStudioActive = ['/routes/my', '/bookmarks', '/interests', '/insights', '/docs', '/developer', '/artworks/my', '/map/my-pins', '/profiles/import', '/profiles/my', '/subscription', '/provenance/my', '/account/settings'].some(p => currentPath.startsWith(p));

  const showBackBtn = currentPath !== '/' && currentPath !== '' && currentPath !== '/map';

  const handleReturnToAdmin = async () => {
    await returnToAdmin();
    navigate('/admin/users');
  };

  return (
    <>
      {isImpersonating && (
        <div style={{
          background: 'linear-gradient(135deg, #F5A623, #e8951a)',
          color: '#1a1a2e',
          padding: '0.5rem 1rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
          fontSize: '0.85rem',
          fontWeight: 600,
          zIndex: 1100,
          position: 'relative',
        }}>
          <span>
            <i className="fas fa-eye"></i> Viewing as {user?.displayName} ({user?.email})
          </span>
          <button
            onClick={handleReturnToAdmin}
            style={{
              background: '#1a1a2e',
              color: '#F5A623',
              border: 'none',
              borderRadius: 8,
              padding: '0.35rem 1rem',
              fontWeight: 700,
              fontSize: '0.8rem',
              cursor: 'pointer',
            }}
          >
            <i className="fas fa-arrow-left"></i> Return to Admin
          </button>
        </div>
      )}
      <nav className="navbar" ref={navRef}>
        <div className="nav-container">
          <Link to="/" className="nav-brand">
            <img src="/images/logo.png" alt="ArtRouting" className="nav-logo" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <span className="nav-brand-text">ArtRouting</span>
          </Link>

          <button
            className={`nav-toggle${menuOpen ? ' open' : ''}`}
            ref={toggleRef}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation"
          >
            <i className="fas fa-bars"></i>
            <i className="fas fa-times"></i>
          </button>

          <div className={`nav-links${menuOpen ? ' open' : ''}`} id="navLinks">
            {/* Explore dropdown */}
            <div className={`nav-dropdown${openDropdowns.has('explore') ? ' open' : ''}`}>
              <button
                className={`nav-link nav-dropdown-btn${isExploreActive ? ' active' : ''}`}
                onClick={(e) => toggleDropdown('explore', e)}
              >
                <i className="fas fa-compass"></i> <T k="nav.explore" /> <i className="fas fa-chevron-down nav-chevron"></i>
              </button>
              <div className="nav-dropdown-menu">
                {isMenuAllowed('map') && (
                  <Link to="/map" className={`nav-link${currentPath === '/map' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                    <i className="fas fa-map-marked-alt"></i> <T k="nav.map" />
                  </Link>
                )}
                {isMenuAllowed('discover') && (
                  <Link to="/discover" className={`nav-link${currentPath.startsWith('/discover') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                    <i className="fas fa-compass"></i> <T k="nav.discover" />
                  </Link>
                )}
                {isMenuAllowed('routes') && (
                  <Link to="/routes" className={`nav-link${currentPath.startsWith('/routes') && !currentPath.startsWith('/routes/my') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                    <i className="fas fa-route"></i> <T k="nav.routes" />
                  </Link>
                )}
              </div>
            </div>

            {/* Art World dropdown */}
            <div className={`nav-dropdown${openDropdowns.has('artworld') ? ' open' : ''}`}>
              <button
                className={`nav-link nav-dropdown-btn${isArtWorldActive ? ' active' : ''}`}
                onClick={(e) => toggleDropdown('artworld', e)}
              >
                <i className="fas fa-palette"></i> <T k="nav.artWorld" /> <i className="fas fa-chevron-down nav-chevron"></i>
              </button>
              <div className="nav-dropdown-menu">
                {isMenuAllowed('profiles') && (
                  <Link to="/profiles" className={`nav-link${currentPath.startsWith('/profiles') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                    <i className="fas fa-palette"></i> <T k="nav.artists" />
                  </Link>
                )}
                {isMenuAllowed('artradar') && (
                  <Link to="/artradar" className={`nav-link${currentPath.startsWith('/artradar') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                    <ArtRadarIcon /> ArtRadar
                  </Link>
                )}
                {isMenuAllowed('events') && (
                  <Link to="/events" className={`nav-link${currentPath.startsWith('/events') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                    <i className="fas fa-calendar-alt"></i> <T k="nav.events" />
                  </Link>
                )}
              </div>
            </div>

            {/* Pricing link — always visible */}
            <Link to="/pricing" className={`nav-link${currentPath === '/pricing' ? ' active' : ''}`} onClick={handleNavLinkClick}>
              <i className="fas fa-tags"></i> <T k="nav.pricing" />
            </Link>

            {/* Messages — top-level; shown when the user has messaging access
                (superadmin-only until opened to tiers via Admin → Messaging). */}
            {isAuthenticated && canMessage && (
              <Link to="/messages" className={`nav-link${currentPath.startsWith('/messages') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                <i className="fas fa-comments"></i> <T k="nav.messages" />
                {unreadMsgs > 0 && (
                  <span style={{ background: 'var(--arrow-red, #B83A4B)', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px', marginLeft: 6 }}>{unreadMsgs}</span>
                )}
              </Link>
            )}

            {/* Authenticated: My ArtRouting dropdown — the central workspace for every role */}
            {isAuthenticated && (
              <div className={`nav-dropdown${openDropdowns.has('studio') ? ' open' : ''}`}>
                <button
                  className={`nav-link nav-dropdown-btn${isStudioActive ? ' active' : ''}`}
                  onClick={(e) => toggleDropdown('studio', e)}
                >
                  <i className="fas fa-th"></i> <T k="nav.myStudio" />{' '}
                  {user?.tier && <TierBadge tier={user.tier} size="small" />}
                  {' '}<i className="fas fa-chevron-down nav-chevron"></i>
                </button>
                <div className="nav-dropdown-menu">
                  {/* ♡ Interests — every member, every tier, no role switch */}
                  {isMenuAllowed('interests') && (
                    <Link to="/interests" className={`nav-link${currentPath.startsWith('/interests') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-heart" style={{ color: '#B83A4B' }}></i> <T k="nav.interests" />
                      {interestsUnread > 0 && (
                        <span style={{ background: 'var(--arrow-red, #B83A4B)', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px', marginLeft: 6 }} aria-label={t('interests.unreadLabel').replace('{n}', String(interestsUnread))}>{interestsUnread}</span>
                      )}
                    </Link>
                  )}
                  {/* Role switch + compact Activity summary → private Insights report */}
                  {isMenuAllowed('insights') && <MenuActivity onNavigate={handleNavLinkClick} />}
                  {isMenuAllowed('my_profile') && (
                    <Link to="/profiles/my" className={`nav-link${currentPath === '/profiles/my' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-user-circle"></i> <T k="nav.myProfile" />
                    </Link>
                  )}
                  {isMenuAllowed('my_pins') && (
                    <Link to="/map/my-pins" className={`nav-link${currentPath.startsWith('/map/my-pins') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-map-pin"></i> <T k="nav.myPins" />
                    </Link>
                  )}
                  {isMenuAllowed('my_artworks') && (
                    <Link to="/artworks/my" className={`nav-link${currentPath.startsWith('/artworks/my') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-image"></i> <T k="nav.myArtworks" />
                    </Link>
                  )}
                  {isMenuAllowed('my_provenance') && (
                    <Link to="/provenance/my" className={`nav-link${currentPath.startsWith('/provenance/my') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-qrcode"></i> <T k="nav.myProvenance" />
                    </Link>
                  )}
                  {isMenuAllowed('my_routes') && (
                    <Link to="/routes/my" className={`nav-link${currentPath.startsWith('/routes/my') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-map-signs"></i> <T k="nav.myRoutes" />
                    </Link>
                  )}
                  {isMenuAllowed('my_events') && (
                    <Link to="/events/my" className={`nav-link${currentPath.startsWith('/events/my') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-calendar-alt"></i> <T k="nav.myEvents" />
                    </Link>
                  )}
                  {isMenuAllowed('my_articles') && (
                    <Link to="/artradar/my" className={`nav-link${currentPath.startsWith('/artradar/my') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-newspaper"></i> <T k="nav.myArticles" />
                    </Link>
                  )}
                  {/* My Artists — curators at Active or above (any role, Shared Tiers)
                      OR admins (admins always see it to test) */}
                  {isMenuAllowed('my_artists') && (
                    (user?.isCurator && tierLevel(user?.tier) >= 1) ||
                    user?.role === 'platform_admin' ||
                    user?.role === 'superadmin'
                  ) && (
                    <Link to="/my-artists" className={`nav-link${currentPath.startsWith('/my-artists') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-user-tie"></i> <T k="nav.myArtists" />
                    </Link>
                  )}
                  {/* My Curators — visible to any authenticated user; page handles empty state */}
                  {isMenuAllowed('my_curators') && (
                    <Link to="/my-curators" className={`nav-link${currentPath.startsWith('/my-curators') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-users"></i> <T k="nav.myCurators" />
                    </Link>
                  )}
                  {/* Saved (bookmarks) now lives inside ♡ Interests; /bookmarks forwards there. */}
                  {isMenuAllowed('authorship') && (
                    <Link to="/authorship" className={`nav-link${currentPath.startsWith('/authorship') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-user-pen"></i> <T k="nav.authorship" />
                    </Link>
                  )}
                  {isMenuAllowed('import_profile') && (
                    <Link to="/profiles/import" className={`nav-link${currentPath.startsWith('/profiles/import') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-globe"></i> <T k="nav.import" />
                    </Link>
                  )}
                  {isMenuAllowed('my_consignments') && (
                    <Link to="/consignments/my" className={`nav-link${currentPath === '/consignments/my' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-paper-plane"></i> My Submissions
                    </Link>
                  )}
                  {isMenuAllowed('consignment_inbox') && (
                    <Link to="/consignments/inbox" className={`nav-link${currentPath === '/consignments/inbox' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-inbox"></i> Consignment Inbox
                    </Link>
                  )}
                  <Link to="/subscription" className={`nav-link${currentPath === '/subscription' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                    <i className="fas fa-credit-card"></i> <T k="nav.subscription" />
                  </Link>
                  <Link to="/account/settings" className={`nav-link${currentPath === '/account/settings' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                    <i className="fas fa-cog"></i> Account Settings
                  </Link>
                  {isMenuAllowed('api_keys') && (
                    <Link to="/developer/api-keys" className={`nav-link${currentPath.startsWith('/developer') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-code"></i> <T k="nav.developerApi" />
                    </Link>
                  )}
                  {isMenuAllowed('docs') && (
                    <Link to="/docs" className={`nav-link${currentPath.startsWith('/docs') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                      <i className="fas fa-file-alt"></i> <T k="nav.docs" />
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* About Us (formerly Contact) — at the end of the menu, after My ArtRouting */}
            <div className={`nav-dropdown${openDropdowns.has('about') ? ' open' : ''}`}>
              <button
                className={`nav-link nav-dropdown-btn${currentPath === '/contact' || currentPath.startsWith('/about') ? ' active' : ''}`}
                onClick={(e) => toggleDropdown('about', e)}
              >
                <i className="fas fa-info-circle"></i> <T k="nav.aboutUs" />{' '}<i className="fas fa-chevron-down nav-chevron"></i>
              </button>
              <div className="nav-dropdown-menu">
                <Link to="/contact" className={`nav-link${currentPath === '/contact' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                  <i className="fas fa-envelope"></i> <T k="footer.contact" />
                </Link>
                <Link to="/about/qa" className={`nav-link${currentPath === '/about/qa' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                  <i className="fas fa-circle-question"></i> <T k="qa.navLabel" />
                </Link>
              </div>
            </div>

            {isAuthenticated && isAdmin && (
              <Link to="/admin" className={`nav-link${currentPath.startsWith('/admin') ? ' active' : ''}`} onClick={handleNavLinkClick}>
                <i className="fas fa-shield-alt"></i> <T k="nav.admin" />
              </Link>
            )}

            <LanguageSwitcher />
            <GoogleTranslateSlot />

            {isAuthenticated && user ? (
              <div className="nav-user">
                <span className="nav-user-name"><i className="fas fa-user-circle"></i> {user.displayName}</span>
                <a href="/auth/logout" className="nav-link nav-logout" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i> <T k="nav.logout" />
                </a>
              </div>
            ) : (
              <>
                <Link to="/auth/login" className={`nav-link${currentPath === '/auth/login' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                  <i className="fas fa-sign-in-alt"></i> <T k="nav.login" />
                </Link>
                <Link to="/auth/register" className={`nav-link btn-nav-register${currentPath === '/auth/register' ? ' active' : ''}`} onClick={handleNavLinkClick}>
                  <i className="fas fa-user-plus"></i> <T k="nav.register" />
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {showToolbar && (
        <>
          <div className="search-filter-sticky" id="search-filter-sticky">
            <FilterAccordion page={filterPage} />
          </div>
          <div className="search-filter-spacer" id="search-filter-spacer" />
        </>
      )}

      <main className="main-content">
        {setupIncomplete && !location.pathname.startsWith('/welcome') && (
          <div style={{
            background: 'rgba(245,166,35,0.12)', borderBottom: '1px solid rgba(245,166,35,0.4)',
            padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            justifyContent: 'center', fontSize: '0.9rem',
          }}>
            <i className="fas fa-list-check" style={{ color: '#F5A623' }}></i>
            <span>{t('welcome.banner').replace('{step}', t(`welcome.status.${setupStatus}`))}</span>
            <Link to="/welcome" className="btn btn-primary" style={{ padding: '0.3rem 0.9rem', fontSize: '0.85rem' }}>
              <T k="welcome.banner.cta" />
            </Link>
          </div>
        )}
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-links">
            <Link to="/legal/impressum"><T k="footer.impressum" /></Link>
            <Link to="/legal/privacy"><T k="footer.privacy" /></Link>
            <Link to="/legal/terms"><T k="footer.terms" /></Link>
            <Link to="/legal/cookies"><T k="footer.cookies" /></Link>
            <Link to="/legal/authorship"><T k="footer.authorshipRules" /></Link>
            <Link to="/legal/messaging"><T k="footer.messagingRules" /></Link>
            <Link to="/contact"><T k="footer.contact" /></Link>
            <Link to="/about/qa"><T k="qa.navLabel" /></Link>
          </div>
          <div className="footer-info">
            <span>&copy; {new Date().getFullYear()} ArtRouting. <T k="footer.tagline" /></span>
            <span className="footer-version">v{APP_VERSION}</span>
          </div>
        </div>
      </footer>

      {/* Superadmin in-place text editing — renders nothing for anyone else */}
      <TextEditOverlay />

      {/* Floating back button */}
      {showBackBtn && (
        <a href="/" className="floating-back-btn" title={t('nav.goBack')} aria-label={t('nav.goBack')} onClick={handleBackClick}>
          <i className="fas fa-arrow-left"></i>
          <span><T k="nav.goBack" /></span>
        </a>
      )}
    </>
  );
}
